import { TokenType } from '../lexer/token-type.js';
import type { Token } from '../lexer/token.js';
import { KEYWORD_LIST, normalizeKeywordText } from '../lexer/keywords.js';
import { Dialect } from '../dialect.js';
import { ParseError } from './parse-error.js';
import type {
  ArrayAccess,
  BinaryOperator,
  ConstantDeclaration,
  Declaration,
  ElseIfBranch,
  Expression,
  ForStatement,
  FunctionDeclaration,
  GlossaProgram,
  Identifier,
  IfStatement,
  LValue,
  ProcedureDeclaration,
  Program,
  PseudoglossaProgram,
  RepeatUntilStatement,
  ReturnType,
  Statement,
  CaseComparisonOperator,
  Subprogram,
  SwitchCase,
  SwitchCaseValue,
  SwitchStatement,
  VariableDeclaration,
  VariableDeclarator,
  WhileStatement,
} from './ast.js';

export interface ParseResult {
  readonly program: Program | null;
  readonly error: ParseError | null;
}

/** Αποτέλεσμα ανάλυσης "τμήματος αλγορίθμου" — βλ. `Parser.parseFragment`. */
export interface ParseFragmentResult {
  readonly statements: readonly Statement[] | null;
  /** ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ δηλώσεις μετά τις εντολές (μόνο ΓΛΩΣΣΑ) — άδειο πίνακα όταν δεν υπάρχουν καθόλου (βλ. `Parser.parseFragment`). */
  readonly subprograms: readonly Subprogram[];
  readonly error: ParseError | null;
}

const VARIABLE_TYPE_KEYWORDS = new Set(['ΑΚΕΡΑΙΕΣ', 'ΠΡΑΓΜΑΤΙΚΕΣ', 'ΧΑΡΑΚΤΗΡΕΣ', 'ΛΟΓΙΚΕΣ']);
/** Ενικές μορφές — μόνο για τον τύπο επιστροφής μιας ΣΥΝΑΡΤΗΣΗΣ (βλ. ast.ts, `ReturnType`). */
const RETURN_TYPE_KEYWORDS = new Set(['ΑΚΕΡΑΙΑ', 'ΠΡΑΓΜΑΤΙΚΗ', 'ΧΑΡΑΚΤΗΡΑΣ', 'ΛΟΓΙΚΗ']);

// Η ΓΛΩΣΣΑ δέχεται μόνο ΔΙΑΒΑΣΕ/ΓΡΑΨΕ για είσοδο/έξοδο· τα ΕΚΤΥΠΩΣΕ/ΕΜΦΑΝΙΣΕ
// και το ΑΝΤΙΜΕΤΑΘΕΣΕ είναι μόνο Ψευδογλώσσας.
const GLOSSA_WRITE_KEYWORDS = new Set(['ΓΡΑΨΕ']);
const PSEUDOGLOSSA_WRITE_KEYWORDS = new Set(['ΓΡΑΨΕ', 'ΕΚΤΥΠΩΣΕ', 'ΕΜΦΑΝΙΣΕ']);

function levenshteinDistance(a: string, b: string): number {
  const dp: number[][] = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) dp[i]![0] = i;
  for (let j = 0; j <= b.length; j++) dp[0]![j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i]![j] = Math.min(dp[i - 1]![j]! + 1, dp[i]![j - 1]! + 1, dp[i - 1]![j - 1]! + cost);
    }
  }
  return dp[a.length]![b.length]!;
}

/**
 * Αν ένα IDENTIFIER απέχει ακριβώς μία επεξεργασία (Levenshtein) από κάποια
 * δεσμευμένη λέξη, το θεωρούμε πιθανό τυπογραφικό λάθος (π.χ. "ΑΛΛΩΣ_ΑΝ"
 * αντί για "ΑΛΛΙΩΣ_ΑΝ") και το προτείνουμε αντί για ένα παραπλανητικό γενικό
 * σφάλμα. Περιοριζόμαστε σε λέξεις μήκους ≥ 4 (και μόνο σε λέξεις-κλειδιά
 * μήκους ≥ 4) γιατί σύντομα αναγνωριστικά όπως "α", "i", "Χ" απέχουν σχεδόν
 * πάντα μία επεξεργασία από κάποια σύντομη λέξη-κλειδί (π.χ. "ΑΝ", "Η") —
 * χωρίς αυτόν τον περιορισμό θα εμφανίζονταν άσχετες προτάσεις για κάθε
 * σύντομο όνομα μεταβλητής.
 */
function suggestKeyword(identifierText: string): string | undefined {
  const normalized = normalizeKeywordText(identifierText);
  if (normalized.length < 4) return undefined;
  let best: string | undefined;
  let bestDistance = Infinity;
  for (const keyword of KEYWORD_LIST) {
    if (keyword.length < 4) continue;
    const distance = levenshteinDistance(normalized, keyword);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = keyword;
    }
  }
  return bestDistance === 1 ? best : undefined;
}

/**
 * Recursive-descent Parser. Καταναλώνει tokens από τον Lexer και χτίζει AST.
 *
 * Σχεδιαστικές αποφάσεις:
 * - Η διάλεκτος (ΓΛΩΣΣΑ/Ψευδογλώσσα) δίνεται ρητά από τον καλούντα — ο
 *   Lexer παράγει το ίδιο υπερσύνολο tokens και για τις δύο.
 * - Σταματάει στο πρώτο συντακτικό σφάλμα (σε αντίθεση με τον Lexer, που
 *   συλλέγει πολλαπλά) — απλούστερο και προβλέψιμο recovery.
 * - ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ (υποπρογράμματα) υποστηρίζονται ΜΟΝΟ σε ΓΛΩΣΣΑ (βλ.
 *   `parseSubprograms`) — η Ψευδογλώσσα δεν έχει αντίστοιχο μηχανισμό.
 */
export class Parser {
  private readonly tokens: readonly Token[];
  private readonly dialect: Dialect;
  private pos = 0;

  constructor(tokens: readonly Token[], dialect: Dialect) {
    this.tokens = tokens;
    this.dialect = dialect;
  }

  parse(): ParseResult {
    try {
      const program = this.parseProgram();
      return { program, error: null };
    } catch (e) {
      if (e instanceof ParseError) {
        return { program: null, error: e };
      }
      throw e;
    }
  }

  /**
   * "Τμήμα αλγορίθμου" — αναλύει μια γυμνή λίστα εντολών, ΧΩΡΙΣ το
   * περίβλημα ΑΛΓΟΡΙΘΜΟΣ/ΠΡΟΓΡΑΜΜΑ...ΤΕΛΟΣ (κατάλληλο για να δείξει κανείς
   * ένα κομμάτι ενός μεγαλύτερου αλγορίθμου, π.χ. μόνο έναν βρόχο). Καμία
   * ΔΕΔΟΜΕΝΑ/ΑΠΟΤΕΛΕΣΜΑΤΑ/ΜΕΤΑΒΛΗΤΕΣ έννοια εδώ — απλώς εντολές μέχρι EOF.
   *
   * Στη ΓΛΩΣΣΑ, δέχεται ΕΠΙΠΛΕΟΝ (προαιρετικά) το σώμα τυλιγμένο σε
   * ΑΡΧΗ...ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ, ΧΩΡΙΣ ΠΡΟΓΡΑΜΜΑ/ΜΕΤΑΒΛΗΤΕΣ γύρω τους — έτσι
   * μπορεί κανείς να επικολλήσει αυτούσιο το σώμα ενός υπάρχοντος
   * προγράμματος (που φυσικά θα έχει ΑΡΧΗ/ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ) χωρίς πρώτα
   * να τα αφαιρέσει με το χέρι. Η Ψευδογλώσσα δεν έχει τέτοιο περίβλημα
   * σώματος (το ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ είναι αποκλειστικά της ΓΛΩΣΣΑΣ — βλ.
   * GLOSSA_ONLY_KEYWORDS), οπότε εκεί αγνοείται αυτή η δυνατότητα.
   *
   * Μετά τις εντολές (με ή χωρίς το περίβλημα ΑΡΧΗ/ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ)
   * μπορούν να ακολουθούν ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ δηλώσεις (μόνο ΓΛΩΣΣΑ — βλ.
   * GLOSSA_ONLY_KEYWORDS), ΑΚΡΙΒΩΣ όπως στο κύριο πρόγραμμα (βλ.
   * `parseSubprograms`) — έτσι μπορεί κανείς να γράψει ένα ΚΑΛΕΣΕ στις
   * εντολές ΚΑΙ τη ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ που καλείται, ή ακόμα και ΜΟΝΟ ένα
   * υποπρόγραμμα χωρίς καθόλου εντολές πριν (`statements` τότε είναι απλώς
   * κενός πίνακας) — χωρίς να χρειάζεται να γράψει ολόκληρο ΠΡΟΓΡΑΜΜΑ γύρω τους.
   */
  parseFragment(): ParseFragmentResult {
    try {
      this.skipTrivia();
      let statements: Statement[];
      if (this.dialect === Dialect.GLOSSA && this.checkKeyword('ΑΡΧΗ')) {
        this.advance();
        statements = this.parseStatementList(new Set(['ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ']));
        this.expectKeyword('ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ', "Αναμενόταν 'ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ'");
        this.requireNewline('Αναμενόταν αλλαγή γραμμής');
      } else {
        statements = this.parseStatementList(new Set(['ΔΙΑΔΙΚΑΣΙΑ', 'ΣΥΝΑΡΤΗΣΗ']), false);
      }
      this.skipTrivia();
      const subprograms = this.parseSubprograms();
      this.skipTrivia();
      this.expect(TokenType.EOF, 'Αναμενόταν τέλος αρχείου');
      return { statements, subprograms, error: null };
    } catch (e) {
      if (e instanceof ParseError) {
        return { statements: null, subprograms: [], error: e };
      }
      throw e;
    }
  }

  // --- Βοηθητικά cursor -----------------------------------------------------

  private peek(offset = 0): Token {
    const index = Math.min(this.pos + offset, this.tokens.length - 1);
    // Το EOF είναι πάντα το τελευταίο token — ασφαλές fallback εκτός ορίων.
    return this.tokens[index] ?? this.tokens[this.tokens.length - 1]!;
  }

  private isAtEnd(): boolean {
    return this.peek().type === TokenType.EOF;
  }

  private advance(): Token {
    const tok = this.peek();
    if (!this.isAtEnd()) this.pos++;
    return tok;
  }

  private check(type: TokenType): boolean {
    return this.peek().type === type;
  }

  private checkKeyword(value: string): boolean {
    const tok = this.peek();
    return tok.type === TokenType.KEYWORD && tok.value === value;
  }

  private match(type: TokenType): boolean {
    if (!this.check(type)) return false;
    this.advance();
    return true;
  }

  private matchKeyword(value: string): boolean {
    if (!this.checkKeyword(value)) return false;
    this.advance();
    return true;
  }

  private describeToken(tok: Token): string {
    if (tok.type === TokenType.EOF) return 'τέλος αρχείου';
    if (tok.type === TokenType.NEWLINE) return 'τέλος γραμμής';
    return `'${tok.lexeme}'`;
  }

  private errorAtCurrent(message: string): ParseError {
    const tok = this.peek();
    return new ParseError(`${message} — βρέθηκε ${this.describeToken(tok)}`, tok.line, tok.column);
  }

  private expect(type: TokenType, message: string): Token {
    if (this.check(type)) return this.advance();
    throw this.errorAtCurrent(message);
  }

  /**
   * Αν αποτύχει, ελέγχει ΠΡΩΤΑ μήπως το τρέχον token είναι τυπογραφικό λάθος
   * κάποιας δεσμευμένης λέξης (βλ. `suggestKeyword`) — π.χ. "ΜΕΤΒΛΗΤΕΣ" αντί
   * για "ΜΕΤΑΒΛΗΤΕΣ" — πριν καταλήξει στο γενικό `message` (που συχνά
   * αναφέρεται σε ΕΝΑ συγκεκριμένο αναμενόμενο keyword, π.χ. "Αναμενόταν
   * 'ΑΡΧΗ'", ενώ το πραγματικό πρόβλημα είναι αλλού, νωρίτερα στη γραμμή —
   * βλ. bug report: "ΜΕΤΒΛΗΤΕΣ" έδινε παραπλανητικό "Αναμενόταν 'ΑΡΧΗ'").
   * Ισχύει για ΚΑΘΕ κλήση `expectKeyword` σε όλον τον parser, όχι μόνο τις
   * γύρω από το ΑΡΧΗ.
   */
  private expectKeyword(value: string, message: string): Token {
    if (this.checkKeyword(value)) return this.advance();
    if (this.check(TokenType.IDENTIFIER)) {
      const tok = this.peek();
      const suggestion = suggestKeyword(tok.lexeme);
      if (suggestion !== undefined) {
        throw new ParseError(`Άγνωστη λέξη-κλειδί '${tok.lexeme}' — μήπως εννοείτε '${suggestion}';`, tok.line, tok.column);
      }
    }
    throw this.errorAtCurrent(message);
  }

  /** Ένας δείκτης/διάσταση πίνακα ξεκινά συνήθως έτσι — χρησιμοποιείται ΜΟΝΟ σαν ένδειξη (heuristic) στο `expectArrayBracketClose`, όχι για πραγματικό parsing. */
  private looksLikeExpressionStart(): boolean {
    const tok = this.peek();
    return (
      tok.type === TokenType.NUMBER ||
      tok.type === TokenType.IDENTIFIER ||
      tok.type === TokenType.LPAREN ||
      tok.type === TokenType.STRING ||
      tok.type === TokenType.MINUS
    );
  }

  /**
   * Κλείσιμο ']' μετά από λίστα διαστάσεων/δεικτών πίνακα χωρισμένων με
   * κόμμα (δηλώσεις πινάκων, ΚΑΙ πρόσβαση σε στοιχείο πίνακα — και τα δύο
   * ίδιο σχήμα: `parseExpression()` / `while (match(COMMA)) parseExpression()`).
   * Αν ΔΕΝ βρεθεί ']' αλλά το τρέχον token μοιάζει με αρχή ΝΕΑΣ έκφρασης
   * (π.χ. "Π[10 20]" — μετά το "10" έρχεται κατευθείαν "20", όχι κόμμα ή
   * ']'), δίνει πιο συγκεκριμένο μήνυμα: πιθανότατα ξεχάστηκε το κόμμα
   * ανάμεσα σε δύο διαστάσεις/δείκτες. Αλλιώς, το γενικό "Αναμενόταν ']'".
   */
  private expectArrayBracketClose(): void {
    if (this.check(TokenType.RBRACKET)) {
      this.advance();
      return;
    }
    if (this.looksLikeExpressionStart()) {
      throw this.errorAtCurrent("Αναμενόταν ']' ή ',' (μήπως λείπει κόμμα ανάμεσα στις διαστάσεις/δείκτες του πίνακα;)");
    }
    throw this.errorAtCurrent("Αναμενόταν ']'");
  }

  /**
   * Διαβάζει ένα όνομα (προγράμματος/αλγορίθμου/διαδικασίας/συνάρτησης) με
   * ΣΥΓΚΕΚΡΙΜΕΝΑ μηνύματα για τα πιο συνηθισμένα λάθη μαθητών — αντί για το
   * γενικό "Αναμενόταν όνομα — βρέθηκε Χ" (μέσω `expect`) που δεν εξηγεί
   * ΓΙΑΤΙ το Χ δεν είναι έγκυρο όνομα:
   * 1. Ξεκινάει με αριθμό (π.χ. "1Α") — ο lexer το σπάει σε NUMBER('1') +
   *    IDENTIFIER('Α') διαδοχικά, οπότε το τρέχον token είναι NUMBER.
   * 2. Περιέχει κενό (π.χ. "Το Πρόγραμμα") — δύο IDENTIFIER tokens στην ΙΔΙΑ
   *    γραμμή, αμέσως το ένα μετά το άλλο.
   * `roleGenitive` είναι η γενική πτώση του ρόλου, π.χ. "του προγράμματος",
   * "του αλγορίθμου", "της διαδικασίας", "της συνάρτησης" — χρησιμοποιείται
   * αυτούσια και στα μηνύματα.
   *
   * @param requireNewlineAfter Στο όνομα Αλγορίθμου Ψευδογλώσσας, ο μαθητής
   *   ζήτησε ρητά να ΜΗΝ επιτρέπεται ΤΙΠΟΤΑ άλλο στην ΙΔΙΑ γραμμή μετά το
   *   όνομα — ούτε καν μια έγκυρη πρώτη εντολή σώματος (π.χ. "Αλγόριθμος Α
   *   Χ ← 5" πρέπει ΠΛΕΟΝ να είναι λάθος, όχι απλά ασυνήθιστο στυλ). Όταν
   *   `true`, μετά το όνομα ΠΡΕΠΕΙ να ακολουθεί αλλαγή γραμμής (ή τέλος
   *   αρχείου) — ο έλεγχος για "κενό στο όνομα" (IDENTIFIER αμέσως μετά)
   *   τρέχει ΠΑΝΤΑ πρώτος (πιο συγκεκριμένο μήνυμα όταν ταιριάζει), ο
   *   γενικός "πρέπει να ακολουθεί αλλαγή γραμμής" πιάνει οτιδήποτε άλλο.
   */
  private parseNameToken(roleGenitive: string, requireNewlineAfter = false): Token {
    if (this.check(TokenType.NUMBER)) {
      const tok = this.peek();
      throw new ParseError(`Το όνομα ${roleGenitive} δεν μπορεί να ξεκινάει με αριθμό`, tok.line, tok.column);
    }
    const nameTok = this.expect(TokenType.IDENTIFIER, `Αναμενόταν το όνομα ${roleGenitive}`);
    if (this.check(TokenType.IDENTIFIER)) {
      const tok = this.peek();
      throw new ParseError(
        `Το όνομα ${roleGenitive} δεν μπορεί να περιέχει κενό — γράψε το σαν μία λέξη (π.χ. με κάτω παύλα '_')`,
        tok.line,
        tok.column,
      );
    }
    if (requireNewlineAfter && !this.check(TokenType.NEWLINE) && !this.check(TokenType.EOF)) {
      const tok = this.peek();
      throw new ParseError(`Μετά το όνομα ${roleGenitive} πρέπει να ακολουθεί αλλαγή γραμμής`, tok.line, tok.column);
    }
    return nameTok;
  }

  /** Παραλείπει NEWLINE/COMMENT tokens — καμία συντακτική σημασία γι' αυτά. */
  private skipTrivia(): void {
    while (this.check(TokenType.NEWLINE) || this.check(TokenType.COMMENT)) {
      this.advance();
    }
  }

  /**
   * Σαν `skipTrivia`, αλλά ΑΠΑΙΤΕΙ να υπάρχει τουλάχιστον μία πραγματική
   * αλλαγή γραμμής ανάμεσα στο προηγούμενο token και το επόμενο μη-trivia
   * token — δηλαδή δύο δομικά στοιχεία (π.χ. μια δήλωση μεταβλητών και το
   * 'ΑΡΧΗ', ή δύο διαδοχικές εντολές) ΔΕΝ επιτρέπεται να βρίσκονται στην
   * ΙΔΙΑ γραμμή. Τέλος αρχείου γίνεται πάντα δεκτό (δεν υπάρχει τίποτα άλλο
   * να "κολλήσει" μετά, οπότε δεν χρειάζεται αλλαγή γραμμής πριν από αυτό).
   *
   * Η ΜΟΝΑΔΙΚΗ εξαίρεση σε αυτόν τον κανόνα σε όλη τη γραμματική είναι η
   * μονογραμμική μορφή "ΑΝ...ΤΟΤΕ...εντολή" της Ψευδογλώσσας, η οποία δεν
   * περνάει καθόλου από εδώ (βλ. `parseIfStatement`).
   */
  private requireNewline(message: string): void {
    let sawNewline = false;
    while (this.check(TokenType.NEWLINE) || this.check(TokenType.COMMENT)) {
      if (this.check(TokenType.NEWLINE)) sawNewline = true;
      this.advance();
    }
    if (!sawNewline && !this.isAtEnd()) {
      throw this.errorAtCurrent(message);
    }
  }

  private operatorFromToken(tok: Token): BinaryOperator {
    switch (tok.type) {
      case TokenType.PLUS:
        return '+';
      case TokenType.MINUS:
        return '-';
      case TokenType.STAR:
        return '*';
      case TokenType.SLASH:
        return '/';
      case TokenType.CARET:
        return '^';
      case TokenType.EQUAL:
        return '=';
      case TokenType.NOT_EQUAL:
        return '<>'; // κανονικοποιεί και το ≠ σε '<>'
      case TokenType.LESS:
        return '<';
      case TokenType.LESS_EQUAL:
        return '<=';
      case TokenType.GREATER:
        return '>';
      case TokenType.GREATER_EQUAL:
        return '>=';
      case TokenType.KEYWORD:
        return tok.value as BinaryOperator; // 'DIV' | 'MOD' | 'ΚΑΙ' | 'Η'
      default:
        throw new Error(`Εσωτερικό σφάλμα: μη αναμενόμενος τελεστής ${tok.type}`);
    }
  }

  // --- Πρόγραμμα --------------------------------------------------------

  private parseProgram(): Program {
    this.skipTrivia();
    const program =
      this.dialect === Dialect.GLOSSA ? this.parseGlossaProgram() : this.parsePseudoglossaProgram();
    this.skipTrivia();
    this.expect(TokenType.EOF, 'Αναμενόταν τέλος αρχείου');
    return program;
  }

  private parseGlossaProgram(): GlossaProgram {
    const startTok = this.expectKeyword(
      'ΠΡΟΓΡΑΜΜΑ',
      "Αναμενόταν 'ΠΡΟΓΡΑΜΜΑ' στην αρχή του προγράμματος ΓΛΩΣΣΑΣ",
    );
    const nameTok = this.parseNameToken('του προγράμματος', true);
    this.skipTrivia();

    const declarations = this.parseDeclarationsSection();

    this.expectKeyword('ΑΡΧΗ', "Αναμενόταν 'ΑΡΧΗ'");
    const body = this.parseStatementList(new Set(['ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ']));
    const endTok = this.expectKeyword('ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ', "Αναμενόταν 'ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ'");
    this.requireNewline('Αναμενόταν αλλαγή γραμμής μετά το ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ');

    // Τα υποπρογράμματα ακολουθούν το κύριο πρόγραμμα — βλ. `parseSubprograms`.
    const subprograms = this.parseSubprograms();

    return {
      kind: 'Program',
      dialect: Dialect.GLOSSA,
      name: nameTok.value as string,
      declarations,
      body,
      subprograms,
      endLine: endTok.line,
      line: startTok.line,
      column: startTok.column,
    };
  }

  /** ΣΤΑΘΕΡΕΣ/ΜΕΤΑΒΛΗΤΕΣ — κοινό και για το κύριο πρόγραμμα ΚΑΙ για κάθε ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ (βλ. βιβλίο: "αντίστοιχο με το τμήμα δηλώσεων ενός προγράμματος"). */
  private parseDeclarationsSection(): Declaration[] {
    const declarations: Declaration[] = [];
    if (this.matchKeyword('ΣΤΑΘΕΡΕΣ')) {
      this.requireNewline('Αναμενόταν αλλαγή γραμμής μετά το ΣΤΑΘΕΡΕΣ');
      declarations.push(...this.parseConstantDeclarations());
    }
    if (this.matchKeyword('ΜΕΤΑΒΛΗΤΕΣ')) {
      this.requireNewline('Αναμενόταν αλλαγή γραμμής μετά το ΜΕΤΑΒΛΗΤΕΣ');
      declarations.push(...this.parseVariableDeclarations());
    }
    return declarations;
  }

  /**
   * Μηδέν ή περισσότερα ΔΙΑΔΙΚΑΣΙΑ/ΣΥΝΑΡΤΗΣΗ μετά το ΤΕΛΟΣ_ΠΡΟΓΡΑΜΜΑΤΟΣ — η
   * σειρά τους δεν παίζει κανένα ρόλο (βλ. βιβλίο, §25.5, ερ.10).
   */
  private parseSubprograms(): Subprogram[] {
    const subprograms: Subprogram[] = [];
    for (;;) {
      if (this.checkKeyword('ΔΙΑΔΙΚΑΣΙΑ')) {
        subprograms.push(this.parseProcedureDeclaration());
      } else if (this.checkKeyword('ΣΥΝΑΡΤΗΣΗ')) {
        subprograms.push(this.parseFunctionDeclaration());
      } else {
        break;
      }
      this.requireNewline('Αναμενόταν αλλαγή γραμμής');
    }
    return subprograms;
  }

  /** Ίδιο σκεπτικό με τον αντίστοιχο έλεγχο στο `parseVariableDeclaratorList` — "Ψ 1" αντί για "Ψ1" ως όνομα παραμέτρου. */
  private parseParameterName(): string {
    const nameTok = this.expect(TokenType.IDENTIFIER, 'Αναμενόταν όνομα παραμέτρου');
    if (this.check(TokenType.NUMBER)) {
      const numTok = this.peek();
      throw new ParseError(
        `Το όνομα παραμέτρου '${String(nameTok.value)}' ακολουθείται απευθείας από αριθμό ('${String(numTok.value)}') — μήπως ήθελες ένα ενιαίο όνομα '${String(nameTok.value)}${String(numTok.value)}' (χωρίς κενό);`,
        numTok.line,
        numTok.column,
      );
    }
    return nameTok.value as string;
  }

  /** "(Α, Β, Γ)" ή "()" — απλά ονόματα παραμέτρων, χωρίς Ο/Ε/ΕΟ διάκριση (βλ. σχόλιο στο ast.ts). */
  private parseParameterList(): string[] {
    this.expect(TokenType.LPAREN, "Αναμενόταν '(' στη λίστα παραμέτρων");
    const params: string[] = [];
    if (!this.check(TokenType.RPAREN)) {
      params.push(this.parseParameterName());
      while (this.match(TokenType.COMMA)) {
        params.push(this.parseParameterName());
      }
    }
    this.expect(TokenType.RPAREN, "Αναμενόταν ')' στο τέλος της λίστας παραμέτρων");
    return params;
  }

  private parseProcedureDeclaration(): ProcedureDeclaration {
    const startTok = this.expectKeyword('ΔΙΑΔΙΚΑΣΙΑ', "Αναμενόταν 'ΔΙΑΔΙΚΑΣΙΑ'");
    const nameTok = this.parseNameToken('της διαδικασίας');
    const parameters = this.parseParameterList();
    this.requireNewline('Αναμενόταν αλλαγή γραμμής');
    const declarations = this.parseDeclarationsSection();
    this.expectKeyword('ΑΡΧΗ', "Αναμενόταν 'ΑΡΧΗ'");
    const body = this.parseStatementList(new Set(['ΤΕΛΟΣ_ΔΙΑΔΙΚΑΣΙΑΣ']));
    const endTok = this.expectKeyword('ΤΕΛΟΣ_ΔΙΑΔΙΚΑΣΙΑΣ', "Αναμενόταν 'ΤΕΛΟΣ_ΔΙΑΔΙΚΑΣΙΑΣ'");
    return {
      kind: 'ProcedureDeclaration',
      name: nameTok.value as string,
      parameters,
      declarations,
      body,
      endLine: endTok.line,
      line: startTok.line,
      column: startTok.column,
    };
  }

  private parseFunctionDeclaration(): FunctionDeclaration {
    const startTok = this.expectKeyword('ΣΥΝΑΡΤΗΣΗ', "Αναμενόταν 'ΣΥΝΑΡΤΗΣΗ'");
    const nameTok = this.parseNameToken('της συνάρτησης');
    const parameters = this.parseParameterList();
    this.expect(TokenType.COLON, "Αναμενόταν ':' και ο τύπος επιστροφής της συνάρτησης (ΑΚΕΡΑΙΑ/ΠΡΑΓΜΑΤΙΚΗ/ΧΑΡΑΚΤΗΡΑΣ/ΛΟΓΙΚΗ)");
    const typeTok = this.peek();
    if (typeTok.type !== TokenType.KEYWORD || !RETURN_TYPE_KEYWORDS.has(String(typeTok.value))) {
      throw this.errorAtCurrent('Αναμενόταν τύπος επιστροφής συνάρτησης (ΑΚΕΡΑΙΑ/ΠΡΑΓΜΑΤΙΚΗ/ΧΑΡΑΚΤΗΡΑΣ/ΛΟΓΙΚΗ)');
    }
    this.advance();
    this.requireNewline('Αναμενόταν αλλαγή γραμμής');
    const declarations = this.parseDeclarationsSection();
    this.expectKeyword('ΑΡΧΗ', "Αναμενόταν 'ΑΡΧΗ'");
    const body = this.parseStatementList(new Set(['ΤΕΛΟΣ_ΣΥΝΑΡΤΗΣΗΣ']));
    const endTok = this.expectKeyword('ΤΕΛΟΣ_ΣΥΝΑΡΤΗΣΗΣ', "Αναμενόταν 'ΤΕΛΟΣ_ΣΥΝΑΡΤΗΣΗΣ'");
    return {
      kind: 'FunctionDeclaration',
      name: nameTok.value as string,
      parameters,
      returnType: typeTok.value as ReturnType,
      declarations,
      body,
      endLine: endTok.line,
      line: startTok.line,
      column: startTok.column,
    };
  }

  private parsePseudoglossaProgram(): PseudoglossaProgram {
    const startTok = this.expectKeyword(
      'ΑΛΓΟΡΙΘΜΟΣ',
      "Αναμενόταν 'Αλγόριθμος' στην αρχή του αλγορίθμου Ψευδογλώσσας",
    );
    const nameTok = this.parseNameToken('του αλγορίθμου', true);
    this.requireNewline('Αναμενόταν αλλαγή γραμμής');

    let dataVariables: string[] = [];
    if (this.matchKeyword('ΔΕΔΟΜΕΝΑ')) {
      dataVariables = this.parseDataFrame();
      this.requireNewline('Αναμενόταν αλλαγή γραμμής');
    }

    const body = this.parseStatementList(new Set(['ΑΠΟΤΕΛΕΣΜΑΤΑ', 'ΤΕΛΟΣ']), false);

    let resultVariables: string[] = [];
    if (this.matchKeyword('ΑΠΟΤΕΛΕΣΜΑΤΑ')) {
      resultVariables = this.parseDataFrame();
      this.requireNewline('Αναμενόταν αλλαγή γραμμής');
    }

    const endTok = this.expectKeyword('ΤΕΛΟΣ', "Αναμενόταν 'Τέλος' στο τέλος του αλγορίθμου");
    const endNameTok = this.parseNameToken("του αλγορίθμου μετά το 'Τέλος'");
    if (endNameTok.value !== nameTok.value) {
      throw new ParseError(
        `Το όνομα μετά το 'Τέλος' ('${String(endNameTok.value)}') δεν ταιριάζει με το όνομα του αλγορίθμου ('${String(nameTok.value)}')`,
        endNameTok.line,
        endNameTok.column,
      );
    }

    return {
      kind: 'Program',
      dialect: Dialect.PSEUDOGLOSSA,
      name: nameTok.value as string,
      dataVariables,
      resultVariables,
      body,
      endLine: endTok.line,
      line: startTok.line,
      column: startTok.column,
    };
  }

  /** "// Α, Β //" μετά από ΔΕΔΟΜΕΝΑ/ΑΠΟΤΕΛΕΣΜΑΤΑ — δύο SLASH, λίστα ονομάτων, δύο SLASH. */
  private parseDataFrame(): string[] {
    this.expect(TokenType.SLASH, "Αναμενόταν '//'");
    this.expect(TokenType.SLASH, "Αναμενόταν '//'");

    const names: string[] = [];
    if (this.check(TokenType.IDENTIFIER)) {
      names.push(this.advance().value as string);
      while (this.match(TokenType.COMMA)) {
        names.push(this.expect(TokenType.IDENTIFIER, 'Αναμενόταν όνομα μεταβλητής').value as string);
      }
    }

    this.expect(TokenType.SLASH, "Αναμενόταν το κλείσιμο '//'");
    this.expect(TokenType.SLASH, "Αναμενόταν το κλείσιμο '//'");
    return names;
  }

  // --- Δηλώσεις (ΓΛΩΣΣΑ) --------------------------------------------------

  private parseConstantDeclarations(): ConstantDeclaration[] {
    const decls: ConstantDeclaration[] = [];
    while (this.check(TokenType.IDENTIFIER)) {
      const nameTok = this.advance();
      this.expect(TokenType.EQUAL, "Αναμενόταν '=' στη δήλωση σταθεράς");
      const value = this.parseExpression();
      decls.push({
        kind: 'ConstantDeclaration',
        name: nameTok.value as string,
        value,
        line: nameTok.line,
        column: nameTok.column,
      });
      this.requireNewline('Αναμενόταν αλλαγή γραμμής');
    }
    return decls;
  }

  private parseVariableDeclarations(): VariableDeclaration[] {
    const decls: VariableDeclaration[] = [];
    while (this.peek().type === TokenType.KEYWORD && VARIABLE_TYPE_KEYWORDS.has(String(this.peek().value))) {
      const typeTok = this.advance();
      this.expect(TokenType.COLON, "Αναμενόταν ':' μετά τον τύπο μεταβλητής");
      const declarators = this.parseVariableDeclaratorList();
      decls.push({
        kind: 'VariableDeclaration',
        declaredType: typeTok.value as VariableDeclaration['declaredType'],
        declarators,
        line: typeTok.line,
        column: typeTok.column,
      });
      this.requireNewline('Αναμενόταν αλλαγή γραμμής');
    }
    return decls;
  }

  private parseVariableDeclaratorList(): VariableDeclarator[] {
    const declarators: VariableDeclarator[] = [];
    for (;;) {
      const nameTok = this.expect(TokenType.IDENTIFIER, 'Αναμενόταν όνομα μεταβλητής');
      const dimensions: Expression[] = [];
      if (this.match(TokenType.LBRACKET)) {
        dimensions.push(this.parseExpression());
        while (this.match(TokenType.COMMA)) {
          dimensions.push(this.parseExpression());
        }
        this.expectArrayBracketClose();
      }
      // Δύο IDENTIFIER δίπλα-δίπλα (π.χ. "ημέρες Γ,") δεν είναι ποτέ σωστό
      // εδώ — είτε ξεχάστηκε το κόμμα ανάμεσα σε δύο ΞΕΧΩΡΙΣΤΑ ονόματα, είτε
      // ήθελε ο μαθητής ΕΝΑ όνομα με κάτω παύλα (βλ. ίδιο σκεπτικό με τον
      // αντίστοιχο έλεγχο στο `parseNameToken`). Χωρίς αυτό, το σφάλμα θα
      // εμφανιζόταν πολύ πιο κάτω (π.χ. "Αναμενόταν 'ΑΡΧΗ'"), μακριά από το
      // πραγματικό σημείο του λάθους.
      if (this.check(TokenType.IDENTIFIER)) {
        const nextTok = this.peek();
        throw new ParseError(
          `Δύο ονόματα μεταβλητών ('${String(nameTok.value)}' και '${String(nextTok.value)}') δεν μπορούν να είναι δίπλα-δίπλα — μήπως ξέχασες το κόμμα ανάμεσά τους, ή μήπως ήθελες ένα ενιαίο όνομα με κάτω παύλα ('${String(nameTok.value)}_${String(nextTok.value)}');`,
          nextTok.line,
          nextTok.column,
        );
      }
      // Όνομα ακολουθούμενο απευθείας από αριθμό (π.χ. "Ψ 1" αντί για "Ψ1")
      // — σχεδόν πάντα ξεχασμένο κενό μέσα σε ΕΝΑ όνομα μεταβλητής (π.χ.
      // Ψ1/Ψ2/Ψ3), όχι έγκυρη σύνταξη πίνακα (θα χρειαζόταν '['). Ίδιο
      // σκεπτικό με τον παραπάνω έλεγχο — δείχνει το πιθανό λάθος αμέσως,
      // αντί για ένα μπερδεμένο "Αναμενόταν αλλαγή γραμμής" πολύ πιο κάτω.
      else if (this.check(TokenType.NUMBER)) {
        const numTok = this.peek();
        throw new ParseError(
          `Το όνομα μεταβλητής '${String(nameTok.value)}' ακολουθείται απευθείας από αριθμό ('${String(numTok.value)}') — μήπως ήθελες ένα ενιαίο όνομα '${String(nameTok.value)}${String(numTok.value)}' (χωρίς κενό);`,
          numTok.line,
          numTok.column,
        );
      }
      declarators.push({ name: nameTok.value as string, dimensions, line: nameTok.line, column: nameTok.column });
      if (!this.match(TokenType.COMMA)) break;
    }
    return declarators;
  }

  // --- Λίστα εντολών -------------------------------------------------------

  private isStopKeyword(stopSet: ReadonlySet<string>): boolean {
    const tok = this.peek();
    return tok.type === TokenType.KEYWORD && stopSet.has(String(tok.value));
  }

  /**
   * @param requireLeadingNewline Πριν την ΠΡΩΤΗ εντολή της λίστας ΠΡΕΠΕΙ να
   *   προηγείται αλλαγή γραμμής (π.χ. μετά το 'ΑΡΧΗ', μετά το 'ΤΟΤΕ', μετά
   *   το 'ΕΠΑΝΑΛΑΒΕ' κ.λπ.) — προεπιλογή `true`. Η ΜΟΝΗ περίπτωση όπου αυτό
   *   ΔΕΝ ισχύει είναι η κορυφή ενός "τμήματος αλγορίθμου" (`parseFragment`),
   *   όπου δεν προηγείται καμία λέξη-κλειδί μετά την οποία να απαιτείται
   *   αλλαγή γραμμής. ΑΝΑΜΕΣΑ σε διαδοχικές εντολές πάντα απαιτείται αλλαγή
   *   γραμμής, ανεξάρτητα από αυτή την παράμετρο.
   */
  private parseStatementList(stopKeywords: ReadonlySet<string>, requireLeadingNewline = true): Statement[] {
    const statements: Statement[] = [];
    if (requireLeadingNewline) {
      this.requireNewline('Αναμενόταν αλλαγή γραμμής');
    } else {
      this.skipTrivia();
    }
    while (!this.isAtEnd() && !this.isStopKeyword(stopKeywords)) {
      statements.push(this.parseStatement(stopKeywords));
      this.requireNewline('Αναμενόταν αλλαγή γραμμής μετά την εντολή');
    }
    return statements;
  }

  private parseLValue(): LValue {
    const nameTok = this.expect(TokenType.IDENTIFIER, 'Αναμενόταν όνομα μεταβλητής');
    const id: Identifier = {
      kind: 'Identifier',
      name: nameTok.value as string,
      line: nameTok.line,
      column: nameTok.column,
    };
    if (this.match(TokenType.LBRACKET)) {
      const indices = [this.parseExpression()];
      while (this.match(TokenType.COMMA)) {
        indices.push(this.parseExpression());
      }
      this.expectArrayBracketClose();
      const result: ArrayAccess = { kind: 'ArrayAccess', array: id, indices, line: id.line, column: id.column };
      return result;
    }
    return id;
  }

  private parseStatement(enclosingStopKeywords: ReadonlySet<string>): Statement {
    if (this.checkKeyword('ΔΙΑΒΑΣΕ')) return this.parseReadStatement();
    const writeKeywords =
      this.dialect === Dialect.GLOSSA ? GLOSSA_WRITE_KEYWORDS : PSEUDOGLOSSA_WRITE_KEYWORDS;
    if (this.peek().type === TokenType.KEYWORD && writeKeywords.has(String(this.peek().value))) {
      return this.parseWriteStatement();
    }
    if (this.dialect === Dialect.PSEUDOGLOSSA && this.checkKeyword('ΑΝΤΙΜΕΤΑΘΕΣΕ')) {
      return this.parseSwapStatement();
    }
    if (this.checkKeyword('ΑΝ')) return this.parseIfStatement();
    if (this.checkKeyword('ΓΙΑ')) return this.parseForStatement();
    if (this.checkKeyword('ΟΣΟ')) return this.parseWhileStatement();
    if (this.checkKeyword('ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ')) return this.parseRepeatUntilStatement();
    if (this.checkKeyword('ΕΠΙΛΕΞΕ')) return this.parseSwitchStatement();
    if (this.checkKeyword('ΚΑΛΕΣΕ')) return this.parseCallStatement();
    if (this.check(TokenType.IDENTIFIER)) return this.parseAssignmentStatement();

    // Το token δεν ξεκινά εντολή. Αν είναι δεσμευμένη λέξη που δεν ανήκει σε
    // αυτό το μπλοκ, είναι πιθανό να λείπει το κλείσιμο του μπλοκ (π.χ.
    // ξεχασμένο ΤΕΛΟΣ_ΑΝ) — αναφέρουμε τι θα το έκλεινε σωστά.
    if (enclosingStopKeywords.size > 0) {
      const options = [...enclosingStopKeywords].map((k) => `'${k}'`).join(' ή ');
      throw this.errorAtCurrent(`Αναμενόταν εντολή ή ${options} (μήπως λείπει κάποιο από αυτά;)`);
    }
    throw this.errorAtCurrent('Αναμενόταν εντολή');
  }

  private parseReadStatement(): Statement {
    const tok = this.expectKeyword('ΔΙΑΒΑΣΕ', "Αναμενόταν 'ΔΙΑΒΑΣΕ'");
    const targets = [this.parseLValue()];
    while (this.match(TokenType.COMMA)) {
      targets.push(this.parseLValue());
    }
    return { kind: 'ReadStatement', targets, line: tok.line, column: tok.column };
  }

  private parseWriteStatement(): Statement {
    const tok = this.advance(); // ΓΡΑΨΕ | ΕΚΤΥΠΩΣΕ | ΕΜΦΑΝΙΣΕ
    const values = [this.parseExpression()];
    while (this.match(TokenType.COMMA)) {
      values.push(this.parseExpression());
    }
    const verb = tok.value as 'ΓΡΑΨΕ' | 'ΕΚΤΥΠΩΣΕ' | 'ΕΜΦΑΝΙΣΕ';
    return { kind: 'WriteStatement', values, verb, line: tok.line, column: tok.column };
  }

  private parseSwapStatement(): Statement {
    const tok = this.expectKeyword('ΑΝΤΙΜΕΤΑΘΕΣΕ', "Αναμενόταν 'ΑΝΤΙΜΕΤΑΘΕΣΕ'");
    const left = this.parseLValue();
    this.expect(TokenType.COMMA, "Αναμενόταν ','");
    const right = this.parseLValue();
    return { kind: 'SwapStatement', left, right, line: tok.line, column: tok.column };
  }

  private parseAssignmentStatement(): Statement {
    const startTok = this.peek();
    const target = this.parseLValue();
    if (!this.check(TokenType.ASSIGN) && target.kind === 'Identifier') {
      const suggestion = suggestKeyword(startTok.lexeme);
      if (suggestion !== undefined) {
        throw new ParseError(
          `Άγνωστη λέξη-κλειδί '${startTok.lexeme}' — μήπως εννοείτε '${suggestion}';`,
          startTok.line,
          startTok.column,
        );
      }
    }
    this.expect(TokenType.ASSIGN, "Αναμενόταν '←' ή '<-'");
    const value = this.parseExpression();
    return { kind: 'AssignmentStatement', target, value, line: target.line, column: target.column };
  }

  private parseIfStatement(): IfStatement {
    const tok = this.expectKeyword('ΑΝ', "Αναμενόταν 'ΑΝ'");
    const condition = this.parseExpression();
    this.expectKeyword('ΤΟΤΕ', "Αναμενόταν 'ΤΟΤΕ'");

    // Μονογραμμική μορφή — μόνο στην Ψευδογλώσσα: "ΑΝ συνθήκη ΤΟΤΕ εντολή",
    // μία εντολή στην ίδια γραμμή με το ΤΟΤΕ, χωρίς ΤΕΛΟΣ_ΑΝ/ΑΛΛΙΩΣ. Η
    // ΓΛΩΣΣΑ απαιτεί πάντα τη μορφή μπλοκ (ΑΝ...ΤΟΤΕ...ΤΕΛΟΣ_ΑΝ).
    if (
      this.dialect === Dialect.PSEUDOGLOSSA &&
      !this.check(TokenType.NEWLINE) &&
      !this.check(TokenType.COMMENT) &&
      !this.isAtEnd()
    ) {
      const singleStatement = this.parseStatement(new Set());
      return {
        kind: 'IfStatement',
        condition,
        thenBranch: [singleStatement],
        elseIfBranches: [],
        elseBranch: null,
        // Καμία μονογραμμική μορφή δεν έχει ΤΕΛΟΣ_ΑΝ — δεν υπάρχει γραμμή να δείξει.
        endLine: null,
        line: tok.line,
        column: tok.column,
      };
    }

    const thenBranch = this.parseStatementList(new Set(['ΑΛΛΙΩΣ_ΑΝ', 'ΑΛΛΙΩΣ', 'ΤΕΛΟΣ_ΑΝ']));

    const elseIfBranches: ElseIfBranch[] = [];
    while (this.matchKeyword('ΑΛΛΙΩΣ_ΑΝ')) {
      const elseIfCondition = this.parseExpression();
      this.expectKeyword('ΤΟΤΕ', "Αναμενόταν 'ΤΟΤΕ'");
      const elseIfBody = this.parseStatementList(new Set(['ΑΛΛΙΩΣ_ΑΝ', 'ΑΛΛΙΩΣ', 'ΤΕΛΟΣ_ΑΝ']));
      elseIfBranches.push({ condition: elseIfCondition, body: elseIfBody });
    }

    let elseBranch: Statement[] | null = null;
    if (this.matchKeyword('ΑΛΛΙΩΣ')) {
      elseBranch = this.parseStatementList(new Set(['ΤΕΛΟΣ_ΑΝ']));
    }

    const endTok = this.expectKeyword('ΤΕΛΟΣ_ΑΝ', "Αναμενόταν 'ΤΕΛΟΣ_ΑΝ'");
    return {
      kind: 'IfStatement',
      condition,
      thenBranch,
      elseIfBranches,
      elseBranch,
      endLine: endTok.line,
      line: tok.line,
      column: tok.column,
    };
  }

  private parseForStatement(): ForStatement {
    const tok = this.expectKeyword('ΓΙΑ', "Αναμενόταν 'ΓΙΑ'");
    const varTok = this.expect(TokenType.IDENTIFIER, 'Αναμενόταν το όνομα του μετρητή');
    const variable: Identifier = {
      kind: 'Identifier',
      name: varTok.value as string,
      line: varTok.line,
      column: varTok.column,
    };
    this.expectKeyword('ΑΠΟ', "Αναμενόταν 'ΑΠΟ'");
    const from = this.parseExpression();
    this.expectKeyword('ΜΕΧΡΙ', "Αναμενόταν 'ΜΕΧΡΙ'");
    const to = this.parseExpression();
    let step: Expression | null = null;
    if (this.matchKeyword('ΜΕ_ΒΗΜΑ')) {
      step = this.parseExpression();
    }
    const body = this.parseStatementList(new Set(['ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ']));
    const endTok = this.expectKeyword('ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ', "Αναμενόταν 'ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ'");
    return { kind: 'ForStatement', variable, from, to, step, body, endLine: endTok.line, line: tok.line, column: tok.column };
  }

  private parseWhileStatement(): WhileStatement {
    const tok = this.expectKeyword('ΟΣΟ', "Αναμενόταν 'ΟΣΟ'");
    const condition = this.parseExpression();
    this.expectKeyword('ΕΠΑΝΑΛΑΒΕ', "Αναμενόταν 'ΕΠΑΝΑΛΑΒΕ'");
    const body = this.parseStatementList(new Set(['ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ']));
    const endTok = this.expectKeyword('ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ', "Αναμενόταν 'ΤΕΛΟΣ_ΕΠΑΝΑΛΗΨΗΣ'");
    return { kind: 'WhileStatement', condition, body, endLine: endTok.line, line: tok.line, column: tok.column };
  }

  private parseRepeatUntilStatement(): RepeatUntilStatement {
    const tok = this.expectKeyword('ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ', "Αναμενόταν 'ΑΡΧΗ_ΕΠΑΝΑΛΗΨΗΣ'");
    const body = this.parseStatementList(new Set(['ΜΕΧΡΙΣ_ΟΤΟΥ']));
    this.expectKeyword('ΜΕΧΡΙΣ_ΟΤΟΥ', "Αναμενόταν 'ΜΕΧΡΙΣ_ΟΤΟΥ'");
    const condition = this.parseExpression();
    return { kind: 'RepeatUntilStatement', body, condition, line: tok.line, column: tok.column };
  }

  private parseSwitchStatement(): SwitchStatement {
    const tok = this.expectKeyword('ΕΠΙΛΕΞΕ', "Αναμενόταν 'ΕΠΙΛΕΞΕ'");
    const discriminant = this.parseExpression();
    this.requireNewline('Αναμενόταν αλλαγή γραμμής');

    const cases: SwitchCase[] = [];
    let elseBody: Statement[] | null = null;

    while (this.matchKeyword('ΠΕΡΙΠΤΩΣΗ')) {
      if (this.matchKeyword('ΑΛΛΙΩΣ')) {
        elseBody = this.parseStatementList(new Set(['ΠΕΡΙΠΤΩΣΗ', 'ΤΕΛΟΣ_ΕΠΙΛΟΓΩΝ']));
        break;
      }
      const values = [this.parseSwitchCaseValue()];
      while (this.match(TokenType.COMMA)) {
        values.push(this.parseSwitchCaseValue());
      }
      const body = this.parseStatementList(new Set(['ΠΕΡΙΠΤΩΣΗ', 'ΤΕΛΟΣ_ΕΠΙΛΟΓΩΝ']));
      cases.push({ values, body });
    }

    // Δεκτή μια ΕΠΙΛΕΞΕ με μόνο ΠΕΡΙΠΤΩΣΗ ΑΛΛΙΩΣ, αλλά όχι μια εντελώς κενή
    // (χωρίς καμία ΠΕΡΙΠΤΩΣΗ) — ίδια συμπεριφορά με τον πραγματικό διερμηνευτή.
    if (cases.length === 0 && elseBody === null) {
      throw new ParseError('Το ΕΠΙΛΕΞΕ χρειάζεται τουλάχιστον μία ΠΕΡΙΠΤΩΣΗ', tok.line, tok.column);
    }

    const endTok = this.expectKeyword('ΤΕΛΟΣ_ΕΠΙΛΟΓΩΝ', "Αναμενόταν 'ΤΕΛΟΣ_ΕΠΙΛΟΓΩΝ'");
    return {
      kind: 'SwitchStatement',
      discriminant,
      cases,
      elseBody,
      endLine: endTok.line,
      line: tok.line,
      column: tok.column,
    };
  }

  /**
   * Μία τιμή μέσα σε ΠΕΡΙΠΤΩΣΗ — τρεις δυνατές μορφές:
   * - Απλή τιμή: "5" (ακριβές ταίριασμα).
   * - Διάστημα: "10..15" (κλειστό, 10 <= x <= 15).
   * - Συγκριτικός τελεστής χωρίς αριστερό μέλος: "> 20" (υπονοούμενο
   *   αριστερό μέλος η έκφραση της ΕΠΙΛΕΞΕ) — δεκτό στο πραγματικό ΓΛΩΣΣΑ,
   *   αλλά ΜΟΝΟ έτσι· "> 20 ΚΑΙ < 40" δεν είναι αποδεκτό.
   */
  private parseSwitchCaseValue(): SwitchCaseValue {
    const comparison = this.tryConsumeCaseComparisonOperator();
    if (comparison !== null) {
      const value = this.parseExpression();
      return { kind: 'CaseComparison', operator: comparison.operator, value, line: comparison.line, column: comparison.column };
    }
    const from = this.parseExpression();
    if (this.match(TokenType.DOT_DOT)) {
      const to = this.parseExpression();
      return { kind: 'CaseRange', from, to, line: from.line, column: from.column };
    }
    return from;
  }

  private tryConsumeCaseComparisonOperator(): { operator: CaseComparisonOperator; line: number; column: number } | null {
    const tok = this.peek();
    let operator: CaseComparisonOperator;
    switch (tok.type) {
      case TokenType.GREATER:
        operator = '>';
        break;
      case TokenType.LESS:
        operator = '<';
        break;
      case TokenType.GREATER_EQUAL:
        operator = '>=';
        break;
      case TokenType.LESS_EQUAL:
        operator = '<=';
        break;
      case TokenType.NOT_EQUAL:
        operator = '<>';
        break;
      default:
        return null;
    }
    this.advance();
    return { operator, line: tok.line, column: tok.column };
  }

  private parseCallStatement(): Statement {
    const tok = this.expectKeyword('ΚΑΛΕΣΕ', "Αναμενόταν 'ΚΑΛΕΣΕ'");
    const nameTok = this.parseNameToken('της διαδικασίας');
    const callee: Identifier = {
      kind: 'Identifier',
      name: nameTok.value as string,
      line: nameTok.line,
      column: nameTok.column,
    };
    const args: Expression[] = [];
    if (this.match(TokenType.LPAREN)) {
      if (!this.check(TokenType.RPAREN)) {
        args.push(this.parseExpression());
        while (this.match(TokenType.COMMA)) {
          args.push(this.parseExpression());
        }
      }
      this.expect(TokenType.RPAREN, "Αναμενόταν ')'");
    }
    return { kind: 'CallStatement', callee, args, line: tok.line, column: tok.column };
  }

  // --- Εκφράσεις (precedence climbing) --------------------------------------
  // Σειρά προτεραιότητας (χαμηλή → υψηλή): Η, ΚΑΙ, ΟΧΙ, σύγκριση, +/-, * / DIV MOD,
  // μοναδιαίο -, ^ (δεξιόστροφο). Η επιλογή αυτή ακολουθεί τη συνηθισμένη
  // μαθηματική σύμβαση (π.χ. -2^2 = -(2^2))· δεν υπάρχει επίσημη προδιαγραφή
  // προτεραιότητας στη ΓΛΩΣΣΑ/Ψευδογλώσσα για επιβεβαίωση.

  private parseExpression(): Expression {
    return this.parseOr();
  }

  private parseOr(): Expression {
    let left = this.parseAnd();
    while (this.checkKeyword('Η')) {
      const opTok = this.advance();
      const right = this.parseAnd();
      left = {
        kind: 'BinaryExpression',
        operator: this.operatorFromToken(opTok),
        left,
        right,
        line: left.line,
        column: left.column,
      };
    }
    return left;
  }

  private parseAnd(): Expression {
    let left = this.parseNot();
    while (this.checkKeyword('ΚΑΙ')) {
      const opTok = this.advance();
      const right = this.parseNot();
      left = {
        kind: 'BinaryExpression',
        operator: this.operatorFromToken(opTok),
        left,
        right,
        line: left.line,
        column: left.column,
      };
    }
    return left;
  }

  private parseNot(): Expression {
    if (this.checkKeyword('ΟΧΙ')) {
      const opTok = this.advance();
      const operand = this.parseNot();
      return { kind: 'UnaryExpression', operator: 'ΟΧΙ', operand, line: opTok.line, column: opTok.column };
    }
    return this.parseComparison();
  }

  private parseComparison(): Expression {
    let left = this.parseAdditive();
    while (
      this.check(TokenType.EQUAL) ||
      this.check(TokenType.NOT_EQUAL) ||
      this.check(TokenType.LESS) ||
      this.check(TokenType.LESS_EQUAL) ||
      this.check(TokenType.GREATER) ||
      this.check(TokenType.GREATER_EQUAL) ||
      this.check(TokenType.ASSIGN)
    ) {
      // Το ASSIGN («←»/«<-») δεν έχει ΚΑΜΙΑ έγκυρη θέση μέσα σε έκφραση — η
      // ανάθεση είναι πάντα εντολή (βλ. `parseAssignmentStatement`, που
      // καταναλώνει το ASSIGN η ίδια, ΠΡΙΝ καν φτάσει εδώ), ποτέ μέρος
      // έκφρασης. Άρα αν το δούμε ΕΔΩ, ο μαθητής έγραψε κάτι σαν "Α<-5" μέσα
      // σε συνθήκη/παρένθεση (π.χ. "ΑΝ Α<-5 ΤΟΤΕ") εννοώντας "Α < -5" — το
      // "<-" έγινε ΕΝΑ token στον lexer λόγω maximal munch (ίδιο φαινόμενο
      // με το πρώην "<--", βλ. lexer.ts). Το ξαναδιαβάζουμε ρητά ως LESS +
      // μοναδιαίο πλην στο δεξί μέλος, αντί για σφάλμα ασυνάρτητου token.
      const opTok = this.advance();
      const rightOperand = this.parseAdditive();
      const right: Expression =
        opTok.type === TokenType.ASSIGN
          ? { kind: 'UnaryExpression', operator: '-', operand: rightOperand, line: opTok.line, column: opTok.column }
          : rightOperand;
      left = {
        kind: 'BinaryExpression',
        operator: opTok.type === TokenType.ASSIGN ? '<' : this.operatorFromToken(opTok),
        left,
        right,
        line: left.line,
        column: left.column,
      };
    }
    return left;
  }

  private parseAdditive(): Expression {
    let left = this.parseMultiplicative();
    while (this.check(TokenType.PLUS) || this.check(TokenType.MINUS)) {
      const opTok = this.advance();
      const right = this.parseMultiplicative();
      left = {
        kind: 'BinaryExpression',
        operator: this.operatorFromToken(opTok),
        left,
        right,
        line: left.line,
        column: left.column,
      };
    }
    return left;
  }

  private parseMultiplicative(): Expression {
    let left = this.parseUnary();
    while (
      this.check(TokenType.STAR) ||
      this.check(TokenType.SLASH) ||
      this.checkKeyword('DIV') ||
      this.checkKeyword('MOD')
    ) {
      const opTok = this.advance();
      const right = this.parseUnary();
      left = {
        kind: 'BinaryExpression',
        operator: this.operatorFromToken(opTok),
        left,
        right,
        line: left.line,
        column: left.column,
      };
    }
    return left;
  }

  private parseUnary(): Expression {
    if (this.check(TokenType.MINUS)) {
      const opTok = this.advance();
      const operand = this.parseUnary();
      return { kind: 'UnaryExpression', operator: '-', operand, line: opTok.line, column: opTok.column };
    }
    return this.parsePower();
  }

  private parsePower(): Expression {
    const base = this.parsePrimary();
    if (this.check(TokenType.CARET)) {
      this.advance();
      const exponent = this.parseUnary(); // δεξιόστροφο: 2^3^2 = 2^(3^2)
      return {
        kind: 'BinaryExpression',
        operator: '^',
        left: base,
        right: exponent,
        line: base.line,
        column: base.column,
      };
    }
    return base;
  }

  private parsePrimary(): Expression {
    const tok = this.peek();

    if (this.check(TokenType.NUMBER)) {
      this.advance();
      return { kind: 'NumberLiteral', value: tok.value as number, line: tok.line, column: tok.column };
    }

    if (this.check(TokenType.STRING)) {
      this.advance();
      return { kind: 'StringLiteral', value: tok.value as string, line: tok.line, column: tok.column };
    }

    if (this.checkKeyword('ΑΛΗΘΗΣ')) {
      this.advance();
      return { kind: 'BooleanLiteral', value: true, line: tok.line, column: tok.column };
    }

    if (this.checkKeyword('ΨΕΥΔΗΣ')) {
      this.advance();
      return { kind: 'BooleanLiteral', value: false, line: tok.line, column: tok.column };
    }

    if (this.match(TokenType.LPAREN)) {
      const expr = this.parseExpression();
      this.expect(TokenType.RPAREN, "Αναμενόταν ')'");
      return expr;
    }

    if (this.check(TokenType.IDENTIFIER)) {
      this.advance();
      const id: Identifier = { kind: 'Identifier', name: tok.value as string, line: tok.line, column: tok.column };

      if (this.match(TokenType.LBRACKET)) {
        const indices = [this.parseExpression()];
        while (this.match(TokenType.COMMA)) {
          indices.push(this.parseExpression());
        }
        this.expectArrayBracketClose();
        return { kind: 'ArrayAccess', array: id, indices, line: id.line, column: id.column };
      }

      if (this.match(TokenType.LPAREN)) {
        const args: Expression[] = [];
        if (!this.check(TokenType.RPAREN)) {
          args.push(this.parseExpression());
          while (this.match(TokenType.COMMA)) {
            args.push(this.parseExpression());
          }
        }
        this.expect(TokenType.RPAREN, "Αναμενόταν ')'");
        return { kind: 'FunctionCall', callee: id, args, line: id.line, column: id.column };
      }

      return id;
    }

    throw this.errorAtCurrent('Αναμενόταν έκφραση');
  }
}

export function parse(tokens: readonly Token[], dialect: Dialect): ParseResult {
  return new Parser(tokens, dialect).parse();
}

export function parseFragment(tokens: readonly Token[], dialect: Dialect): ParseFragmentResult {
  return new Parser(tokens, dialect).parseFragment();
}
